import React from 'react';
import { uniq } from 'lodash';
import { DESCENDING } from 'constants/sortDirections';
import noop from 'utils/noop';
import { arrayToObject } from 'utils/helpers';
import { getItem, setItem } from 'utils/localStorage';
import { routerMiddlewareHistory } from 'store';
import Body from './Body';
import Cell from './Cell';
import * as cellTypes from './cellTypes';
import BulkActionsRow from './BulkActionsRow';
import Toolbar from './Toolbar';
import FilterInput from './FilterInput';
import Pagination from './Pagination';
import Row from './Row';
import { getColumns } from './utils';
import {
  TableContainer,
  StyledPageSizeSelector,
  PaginationContainer,
  FilterContainer,
} from './Table.styled';

type Props = {
  /** Appearance of the table. */
  appearance: 'normal' | 'iterable';
  /** Actions to show in bulk actions.
   *
   * [{<br>
   *  **label**: _string_ - Label of the aciton.<br><br>
   *  **onClick(e, tableProps)**: _function_ - Callback functionwhen action is clicked.<br>
   *    ```e```: _Object_: The click event.<br><br>
   *    ```tableProps```: _Object_: The table props with including the updated selected rows.<br><br>
   *  **options**: _Array_ - Passing options will render a dropdown. The onClick function will return the selected value in the tableProps paramter.<br><br>
   *  **otherProps**: _Object_ - Additional props for the action button.<br><br>
   * }]
   */
  bulkActions?: [];
  /** Additional content to display in the bulk actions container. */
  bulkActionsContent?: React.ReactNode;
  /** The component to use for the table cell in a row. */
  CellComponent: React.Component;
  /** The component to use for the table rows. */
  RowComponent: React.Component;
  /** @private */
  children?: any;
  /** An additional table class. */
  className?: string;
  /** An additional table body class. */
  classNameBody?: string;
  /**
   * The columns of the table to generate the table headers and rows.<br>
   * Array of columns definition objects.
   *
   * A column object contains:
   *  {<br>
   *  **actionCell**: _object_ - Adds an action cell to each cell of the column.<br>
   *
   *  ```html
   *  <b>className</b>: <i>string</i> - Additional classes for the action cell.
   *  <b>contents</b> <i>node</i> - Content to show in the action cell.
   *  <b>hasDropdown</b>: <i>boolean</i> - If cell contains a dropdown. Applies styles to open dropdown correctly.
   *  <b>header</b>: <i>node</i> - Content to show in the header cell action cell.
   *  <b>disabled</b>: <i>boolean</i> - Actions are disabled.
   *  <b>getContents(rowData)</b>: <i>function</i> - Function to get content of action cell. Overrides `contents` prop.
   *    rowData: <i>object</i> - Data of the row.<br>
   *  <b>onClickEdit(e, rowData)</b>: <i>boolean</i> -  Function to show edit button and calls function when clicked.
   *    e: <i>object</i> - The click event.
   *    rowData: <i>object</i> - Data of the row.<br>
   *  <b>onClickDelete</b>: <i>boolean</i> - Function to show delete button and calls function when clicked.
   *    e: <i>object</i> - The click event.
   *    rowData: <i>object</i> - Data of the row.
   *  ```
   *
   *  **name**: _string_ - Name of the column (Header title).<br><br>
   *  **formatter(value, rowData)**: _function_ - Formats value to display.<br>
   *    ```value```: _string_ - value of the row.<br>
   *    ```rowData```: _Object_ - The data object of the row.<br><br>
   *  **formatUrl(url, rowData)** _function_ - Formats the url of the link.<br>
   *    ```url```: _string_ - The value of the url.<br>
   *    ```rowData```: _Object_ - The data object of the row.<br><br>
   *  **getCellContent(props)**: _function_ - Returns the contents of a custom cell.<br>
   *    ```props```: _object_ - props of the row.<br><br>
   *  **getToggleValue(toggleValue, rowData)**: _function_ - Gets the toggle state of the toggle.<br>
   *    ```toggleValue```: _Object_: The value of the toggleValue given by the `value` props.<br>
   *    ```rowData```: _Object_: The data object of the row.<br><br>
   *  **getTooltip(row)**: _function_ - Function that returns the tooltip value. Only available on text cells.<br>
   *    ```row```: _Object_: The row data.<br><br>
   *  **minWidth**: _number_ - Min width of the row in pixels.<br><br>
   *  **maxWidth**: _number_ - Max width of the row in pixels (MUST leave one column without max width to scale table width).<br><br>
   *  **sortable**: _Boolean_ - If column is sortable.<br><br>
   *  **title**: _Boolean_ - Show display value as title tag.<br><br>
   *  **type**: _string_ - The type of the column and cells under the column.<br><br>
   *  **url**: _string_ - The value of the url.<br><br>
   *  **value**: _string_ - The value prop of the column to retrieve from row data to generate cell data.<br><br>
   * }
   *
   */
  columns: any[];
  /** Docks table on bottom when table reaches bottom of screen. */
  dockOnBottom: boolean;
  /** Docks the left-most column. */
  dockLeftColumn: boolean;
  /** Messsage Header to show when table has no items. */
  emptyStateHeader?: string;
  /** Messsage to show when table has no items. */
  emptyStateText: string;
  /** Additional props when displaying EmptyState. See: `EmptyState` component. */
  emptyStateProps: any;
  /** If previous column is expanded and should take over the space of entire row. */
  expandColumn?: boolean;
  /** Initial value of filter. */
  filter: string;
  /** Debounce time of filter input. Set to 0 for no debounce. */
  filterDebounceTime?: number;
  /** Placeholder of filter input. */
  filterPlaceholder: string;
  /** If pagination is hidden */
  hidePagination?: boolean;
  /** The history object. */
  history: any;
  /** Highlights rows on hover. */
  hoverableRows: boolean;
  /** Whether or not the table is loading. Adds loading overlay. */
  isLoading: boolean;
  /** The name of a single item in the table. */
  itemName: string;
  /** The plural name of the items in the table. */
  itemNamePlural: string;
  /** If loading spinner is inverted. */
  loadingInverted: boolean;
  /**
   * Function to run when filter is changed.
   *
   * ```
   * onChangeFilter(e, filterProps) => {}
   * ```
   * @param { Object } e: The change event.
   * @param { Object } filterProps: New `filter` prop { filter }.
   * @returns { void }
   */
  onChangeFilter?: (e: any, filterProps: any) => void;
  /**
   * Function to run when page number is changed.
   *
   * ```
   * onChangePage(e, pageProps) => {}
   * ```
   * @param { Object } e: The click event.
   * @param { Object } pageProps: New `page` prop { page }.
   * @returns { void }
   */
  onChangePage: (e: any, pageProps: { page: number }) => void;
  /**
   * Function to run when page size is changed.
   *
   * ```
   * onChangePageSize(e, pageSizeProps) => {}
   * ```
   * @param { Object } e: The click event.
   * @param { Object } pageSizeProps: New `pageSize` prop { pageSize }.
   * @returns { void }
   */
  onChangePageSize: (e: any, pageSizeProps: { pageSize: number }) => void;
  /**
   * Function to run when a sorting header is clicked.
   *
   * ```
   * onChangeSort(e, sortProps) => {}
   * ```
   * @param { Object } e: The click event.
   * @param { Object } sortProps: New `orderBy` and `sorting` props { orderBy, sorting}.
   * @returns { void }
   */
  onChangeSort: (e: any, sortProps: any) => void;
  /**
   * Callback function when row is double clicked.
   *
   * ```
   * onDoubleClickRow(e, rowProps) => {}
   * ```
   * @param { Object } e: The double click event.
   * @param { Object } rowData: The data object of the row.
   * @returns { void }
   */
  onDoubleClickRow: (e: any, rowData: any) => void;
  /**
   * Callback function when a row selected state changes.
   *
   * ```
   * onSelectRow(e, rowProps) => {}
   * ```
   * @param { Object } e: The click event.
   * @param { Object } rowProps: Props of selected rows including array of updated selected rows.
   * @returns { void }
   */
  onSelectRow: (e: any, rowProps: any) => void;
  /**
   * Callback when the customize columns modal opens.
   *
   * ```
   * onChangeVisibleColumns() => {}
   * ```
   * @returns { void }
   */
  onOpenCustomizeColumnsModal: () => void;
  /**
   * Function to run when visible columns is changed.
   *
   * ```
   * onChangeVisibleColumns(e, pageProps) => {}
   * ```
   * @param { Object } e: The change event.
   * @param { Object } visibleColumnsProps: New `visibleColumns` prop { visibleColumns }.
   * @returns { void }
   */
  onChangeVisibleColumns: (e: any, visibleColumnsProps: any[]) => void;
  /**
   * Callback function when the select all checkbox is clicked.
   *
   * ```
   * onSelectAllRows(e) => {}
   * ```
   * @param { Object } e: The click event.
   * @returns { void }
   */
  onSelectAllRows: (e: any) => void;
  /** Current sort column. */
  orderBy: string;
  /** Current page. */
  page: number;
  /** Current page size. */
  pageSize: number;
  /** Available page sizes for the table. */
  pageSizes: number[];
  /** Show Page Size in toolbar. */
  pageSizeInToolbar: boolean;
  /**
   * Array of quick filters to show.
   * {
   *  onClick(): Function to run when filter is clicked.
   *  text: Display text of the filter.
   * }
   */
  // quickFilters: [],
  /** Date range start for text filter */
  rangeStart?: number;
  /** Date range end for text filter */
  rangeEnd?: number;
  /** Array of formatted data objects to use in each row component. */
  rows: any[];
  /** The row value prop. */
  rowValueProp: string;
  /** Array of currently selected rows. */
  selectedRows?: [];
  /** If rows are selectable. */
  selectableRows: boolean;
  /** Show vertical table lines to separate columns. */
  showColumnLines: boolean;
  /* If the text filter input is shown */
  showFilterInput?: boolean;
  /* Shows and allows toggling of columns. */
  showToggleColumns: boolean;
  /** Current sort direction. */
  sorting: string;
  /** Add additional content to toolbar if shown. */
  toolbarContent: React.ReactNode;
  /** Total number of items in the table. */
  total: number;
  /** Array of columns visible. If empty, show all columns. */
  visibleColumns: any[];
  /** If passed in, table will store the columns in localStorage of given id. MUST BE UNIQUE! */
  visibleColumnsLocalStorageId?: string;
};

/**
 * Basic table for displaying items.<br>
 * This is deisgned where pagination, sorting and filtering should depend on the backend by
 * passing parameters to the API.
 * <br><br>
 */

export default class Table extends React.Component<Props, any> {
  static Body = Body;
  static Row = Row;
  static Cell = Cell;
  static defaultProps = {
    appearance: 'iterable',
    CellComponent: Cell,
    columns: [],
    dockOnBottom: false,
    dockLeftColumn: false,
    emptyStateText: 'No Results',
    emptyStateProps: {},
    hidePagination: false,
    history: routerMiddlewareHistory,
    hoverableRows: true,
    isLoading: false,
    itemName: 'item',
    itemNamePlural: 'items',
    loadingInverted: false,
    onChangeFilter: noop,
    onChangePage: noop,
    onChangePageSize: noop,
    onChangeSort: noop,
    onChangeVisibleColumns: noop,
    onDoubleClickRow: noop,
    onOpenCustomizeColumnsModal: noop,
    onSelectRow: noop,
    onSelectAllRows: noop,
    orderBy: 'id',
    pageSize: 10,
    pageSizeInToolbar: false,
    RowComponent: Row,
    rows: [],
    rowValueProp: 'id',
    selectableRows: false,
    showColumnLines: false,
    showFilterInput: false,
    showToggleColumns: false,
    sorting: DESCENDING,
    visibleColumns: [],
  };

  constructor(props: any) {
    super(props);

    this.state = {
      visibleColumns: props.visibleColumns,
    };
  }

  isIterableStyle = () => this.props.appearance === 'iterable';

  /**
   * resetVisibleColumns
   * Resets the component state visible columns.
   * @returns { void }
   */
  resetVisibleColumns = () => {
    this.setState({ visibleColumns: [] });
  };

  /**
   * componentDidMount
   * Initializes history listener to check if route changes to updated visible columns in component state.
   * @returns { void }
   */
  componentDidMount = () => {
    const { history: routeHistory } = this.props;
    // @ts-ignore
    this.unlisten = routeHistory.listen(this.resetVisibleColumns);
  };

  /**
   * componentWillUnmount
   * Deregisters history listener.
   * @returns { void }
   */
  componentWillUnmount = () => {
    // @ts-ignore
    this.unlisten();
  };

  /**
   * getLocalStorageVisibleColumnsId
   * Returns the localStorage id for storing the visible columns.
   * @returns { String } The localStorage id.
   */
  getLocalStorageVisibleColumnsId = () => {
    const { visibleColumnsLocalStorageId } = this.props;
    return `tableVisibleColumns_${visibleColumnsLocalStorageId}`;
  };

  /**
   * getVisibleColumns
   * Helper function to get visible columns either in localStorage or in state passed in by props.
   * @returns { Array } The list of visible columns.
   */
  getVisibleColumns = () => {
    const { visibleColumns } = this.state;
    const { visibleColumnsLocalStorageId, columns } = this.props;
    const columnsObject = arrayToObject(columns, 'value');
    if (visibleColumnsLocalStorageId) {
      // @ts-ignore
      return uniq(getItem(this.getLocalStorageVisibleColumnsId(), [])).filter(
        (col: any) => columnsObject[col],
      );
    }
    return visibleColumns;
  };

  /**
   * getColumns
   * Helper function to get the available columns. Filters out any hidden columns.
   * @returns { Array } The available columns to show in the table.
   */
  getColumns = () => {
    const { columns } = this.props;
    const visibleColumns = this.getVisibleColumns();
    return getColumns(columns, visibleColumns);
  };

  /**
   * handleToggleColumns
   * Callback function when visibleColumns have changed.
   * @param { Object } e: The change event.
   * @param { Object } columnProps: Updated props contatining updated visibleColumns.
   * @returns { void }
   */
  handleToggleColumns = (e: any, { visibleColumns, ...otherProps }: any) => {
    const { onChangeVisibleColumns, visibleColumnsLocalStorageId } = this.props;
    onChangeVisibleColumns(e, { ...otherProps, visibleColumns });

    if (visibleColumnsLocalStorageId) {
      setItem(this.getLocalStorageVisibleColumnsId(), visibleColumns);
    }
    this.setState({ visibleColumns });
  };

  /**
   * isToolbarShown
   * Determines if the table toolbar is shown.
   * @returns { Boolean } If the toolbar is shown.
   */
  isToolbarShown = () => {
    const { pageSizeInToolbar, showToggleColumns, toolbarContent } = this.props;
    return pageSizeInToolbar || showToggleColumns || !!toolbarContent;
  };

  /**
   * Renders the bulk actions bar.
   *
   * ```
   * renderBulkActions()
   * ```
   * @param { Boolean } actionsAsButtons: If actions should be rendered as styled buttons.
   * @returns { React.Component } The bulk actions.
   */
  renderBulkActions = (actionsAsButtons?: boolean) => {
    const {
      bulkActions,
      bulkActionsContent,
      itemName,
      itemNamePlural,
      onSelectRow,
      selectableRows,
      selectedRows,
    } = this.props;
    const hasSelectedRows = selectableRows && selectedRows && selectedRows.length;

    const bulkActionsContainer = (
      <BulkActionsRow
        actions={bulkActions}
        actionsAsButtons={actionsAsButtons}
        content={bulkActionsContent}
        isTableButtonStyle={this.isIterableStyle()}
        itemName={itemName}
        itemNamePlural={itemNamePlural}
        selectedRows={selectedRows}
        onClearSelection={onSelectRow}
      />
    );
    const hasBulkContent = bulkActionsContent || (bulkActions && bulkActions.length);
    return hasSelectedRows && hasBulkContent ? bulkActionsContainer : undefined;
  };

  /**
   * Renders the filter input.
   *
   * ```
   * renderFilterInput()
   * ```
   * @returns { React.Component } The filter input.
   */
  renderFilterInput = () => {
    const { filter, filterDebounceTime, filterPlaceholder, onChangeFilter } = this.props;
    return (
      <FilterInput
        debounceTime={filterDebounceTime}
        filter={filter}
        onChange={onChangeFilter}
        placeholder={filterPlaceholder}
      />
    );
  };

  /**
   * Renders the page size selector.
   *
   * ```
   * renderPageSizeSelector()
   * ```
   * @returns { React.Component } The page size selector.
   */
  renderPageSizeSelector = () => {
    const { onChangePageSize, pageSize, pageSizes } = this.props;
    return (
      <StyledPageSizeSelector
        onChangePageSize={onChangePageSize}
        pageSize={pageSize}
        pageSizes={pageSizes}
      />
    );
  };

  /**
   * Renders the pagination component.
   *
   * ```
   * renderPagination()
   * ```
   * @returns { React.Component } The pagination component.
   */
  renderPagination = () => {
    const { onChangePage, pageSize, page, total } = this.props;
    const pages = Math.ceil(total / pageSize);
    return (
      <Pagination
        isTableButtonStyle={this.isIterableStyle()}
        pages={pages}
        onChangePage={onChangePage}
        page={page}
      />
    );
  };

  /**
   * Renders the table.
   *
   * ```
   * renderTable()
   * ```
   * @returns { React.Component } The table.
   */
  renderTable = () => {
    const {
      CellComponent,
      classNameBody,
      dockOnBottom,
      dockLeftColumn,
      emptyStateHeader,
      emptyStateText,
      emptyStateProps,
      expandColumn,
      hoverableRows,
      isLoading,
      loadingInverted,
      onChangeSort,
      onDoubleClickRow,
      onSelectRow,
      onSelectAllRows,
      RowComponent,
      rowValueProp,
      selectedRows,
      selectableRows,
      orderBy,
      page,
      pageSize,
      rows,
      sorting,
      total,
    } = this.props;

    const emptyProps = {
      header: emptyStateHeader,
      text: emptyStateText,
      ...emptyStateProps,
    };

    return (
      <Body
        bulkActionsContent={!this.isToolbarShown() && this.renderBulkActions()}
        className={classNameBody}
        columns={this.getColumns()}
        cellClassName="coreTableCell"
        CellComponent={CellComponent}
        dockOnBottom={dockOnBottom}
        dockLeftColumn={dockLeftColumn}
        emptyStateProps={emptyProps}
        expandColumn={expandColumn}
        headerCellClassName="coreTableHeaderCell"
        headerRowClassName="coreTableHeaderRow"
        hoverableRows={hoverableRows}
        isLoading={isLoading}
        isTableButtonStyle={this.isIterableStyle()}
        isToolbarShown={this.isToolbarShown()}
        loadingInverted={loadingInverted}
        onChangeSort={onChangeSort}
        onDoubleClickRow={onDoubleClickRow}
        onSelectRow={onSelectRow}
        onSelectAllRows={onSelectAllRows}
        orderBy={orderBy}
        page={page}
        pageSize={pageSize}
        rows={rows}
        rowClassName="coreTableRow"
        RowComponent={RowComponent}
        rowValueProp={rowValueProp}
        selectedRows={selectedRows}
        selectableRows={selectableRows}
        sorting={sorting}
        total={total}
      />
    );
  };

  render() {
    const {
      appearance,
      className,
      columns,
      dockLeftColumn,
      hidePagination,
      onOpenCustomizeColumnsModal,
      pageSizeInToolbar,
      showFilterInput,
      showToggleColumns,
      toolbarContent,
    } = this.props;

    const visibleColumns = this.getVisibleColumns();
    const filterTextInput = showFilterInput ? this.renderFilterInput() : undefined;
    const toolbarShown = this.isToolbarShown();

    return (
      <TableContainer
        className={className}
        data-appearance={appearance}
        $isToolbarShown={toolbarShown}
      >
        {!toolbarShown && showFilterInput && <FilterContainer>{filterTextInput}</FilterContainer>}
        {toolbarShown && (
          <Toolbar
            content={toolbarContent}
            columns={columns}
            filterInput={filterTextInput}
            leftColumnDocked={dockLeftColumn}
            isTableButtonStyle={this.isIterableStyle()}
            onOpenToggleModal={onOpenCustomizeColumnsModal}
            onToggleColumns={this.handleToggleColumns}
            overlayContent={this.renderBulkActions(true)}
            pagingControls={pageSizeInToolbar ? this.renderPageSizeSelector() : undefined}
            showToggleColumns={showToggleColumns}
            visibleColumns={visibleColumns}
          />
        )}
        {this.renderTable()}
        {!hidePagination && (
          <PaginationContainer>
            {this.renderPagination()}
            {!pageSizeInToolbar && this.renderPageSizeSelector()}
          </PaginationContainer>
        )}
      </TableContainer>
    );
  }
}

export { Body, Row, Cell, cellTypes };
